import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import toast from "react-hot-toast";
import { FiCreditCard, FiClock, FiCheckCircle, FiCalendar, FiAward, FiExternalLink } from "react-icons/fi";

import { fetchEventList, fetchEventDetails } from "../../utils/eventApi";
import ApprovalQueue from "./components/ApprovalQueue";
import { getStatusBadgeClass, getStatusLabel } from "./EventsList";

/**
 * Reward Coin Approval, as its own page.
 *
 * The same queue also lives as a tab on an event's detail page, but paying
 * winners is its own job rather than a step inside inspecting one event — so it
 * gets a place in the sidebar and an event picker, and you land here already
 * looking at the events that owe something.
 *
 * The picker defaults to the most recently finished event, because a queue only
 * exists once an event is finalized; a draft has nothing to approve.
 */
const RewardCoinApproval = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventId, setEventId] = useState(searchParams.get("event") || "");
  const [detail, setDetail] = useState(null);
  const [summary, setSummary] = useState(null);

  // Completed events first — those are the ones with a payout queue.
  const loadEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const res = await fetchEventList({ page: 1, limit: 100 });
      const list = res?.status ? res.data || [] : [];
      const ordered = [
        ...list.filter((e) => e.status === "completed"),
        ...list.filter((e) => e.status !== "completed"),
      ];
      setEvents(ordered);

      setEventId((current) => {
        if (current && ordered.some((e) => String(e.id) === String(current))) return current;
        return ordered.length > 0 ? String(ordered[0].id) : "";
      });
    } catch (err) {
      toast.error("Failed to load events");
    } finally {
      setEventsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Keep the chosen event in the URL so this page can be linked and reloaded.
  useEffect(() => {
    if (!eventId) return;
    setSearchParams({ event: String(eventId) }, { replace: true });
  }, [eventId, setSearchParams]);

  useEffect(() => {
    if (!eventId) {
      setDetail(null);
      return;
    }
    let cancelled = false;

    fetchEventDetails(eventId)
      .then((res) => {
        if (!cancelled && res?.status) setDetail(res.data);
      })
      .catch(() => {
        if (!cancelled) setDetail(null);
      });

    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const selected = events.find((e) => String(e.id) === String(eventId));

  const cards = [
    {
      label: "Total Winners",
      value: summary?.totalWinners ?? null,
      icon: FiAward,
      tone: "text-gray-800",
      bg: "bg-gray-100 text-gray-600",
    },
    {
      label: "Pending Approvals",
      value: summary?.pending ?? null,
      icon: FiClock,
      tone: "text-amber-600",
      bg: "bg-amber-50 text-amber-600",
      // What the number is actually made of, per currency.
      foot: summary?.owed?.length
        ? summary.owed.map(({ currency, amount }) => `${amount.toLocaleString()} ${currency}`).join(" · ")
        : null,
    },
    {
      label: "Credited Rewards",
      value: summary?.paid ?? null,
      icon: FiCheckCircle,
      tone: "text-emerald-600",
      bg: "bg-emerald-50 text-emerald-600",
    },
    {
      label: "Event Status",
      value: detail?.status ? getStatusLabel(detail.status) : null,
      isText: true,
      icon: FiCheckCircle,
      tone: "text-blue-600",
      bg: "bg-blue-50 text-blue-600",
      foot: detail?.name || null,
    },
    {
      label: "Schedule",
      value: detail?.date_range_str || null,
      isText: true,
      icon: FiCalendar,
      tone: "text-gray-800",
      bg: "bg-gray-100 text-gray-600",
      foot: detail?.total_days ? `${detail.total_days} day${detail.total_days === 1 ? "" : "s"}` : null,
    },
  ];

  return (
    <div className="flex h-screen bg-gray-50/50">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Navbar />

        <main className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <FiCreditCard className="text-blue-600" /> Reward Coin Approval
                </h1>
                <p className="text-xs text-gray-400 mt-1">
                  Approve every reward a finalized event owes. Nothing is credited until it is
                  approved here — PX included.
                </p>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-xl shadow-sm">
                  <FiAward className="text-blue-600 shrink-0" size={14} />
                  <select
                    value={eventId}
                    onChange={(e) => setEventId(e.target.value)}
                    disabled={eventsLoading || events.length === 0}
                    className="text-xs font-semibold text-gray-800 bg-transparent focus:outline-none max-w-[240px] truncate cursor-pointer"
                  >
                    {eventsLoading && <option value="">Loading events…</option>}
                    {!eventsLoading && events.length === 0 && <option value="">No events</option>}
                    {events.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.name} ({getStatusLabel(e.status)})
                      </option>
                    ))}
                  </select>
                </div>

                {eventId && (
                  <button
                    onClick={() => navigate(`/event/${eventId}`)}
                    className="px-3 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 text-xs font-medium rounded-xl transition shadow-sm flex items-center gap-1.5"
                    title="Open this event"
                  >
                    <FiExternalLink size={14} /> Open event
                  </button>
                )}
              </div>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {cards.map((card) => {
                const Icon = card.icon;
                return (
                  <div
                    key={card.label}
                    className="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider truncate">
                          {card.label}
                        </p>
                        <p
                          className={`font-bold mt-1 ${card.tone} ${
                            card.isText ? "text-sm truncate" : "text-2xl"
                          }`}
                        >
                          {card.value === null || card.value === undefined ? (
                            <span className="text-gray-300 text-base">—</span>
                          ) : card.isText ? (
                            card.value
                          ) : (
                            card.value.toLocaleString()
                          )}
                        </p>
                        {card.foot && (
                          <p className="text-[10px] text-gray-400 mt-0.5 truncate" title={card.foot}>
                            {card.foot}
                          </p>
                        )}
                      </div>
                      <span
                        className={`w-9 h-9 shrink-0 rounded-xl flex items-center justify-center ${card.bg}`}
                      >
                        <Icon size={16} />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* A queue only exists once an event has been finalized. */}
            {selected && selected.status !== "completed" && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl text-xs text-amber-800 flex items-start gap-2.5">
                <FiClock className="mt-0.5 shrink-0 text-amber-600" size={16} />
                <span>
                  <strong>{selected.name}</strong> is{" "}
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-medium ${getStatusBadgeClass(
                      selected.status
                    )}`}
                  >
                    {getStatusLabel(selected.status)}
                  </span>
                  . Winners and their payouts are worked out when the event is finalized, so there is
                  nothing to approve until then.
                </span>
              </div>
            )}

            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
              <ApprovalQueue
                eventId={eventId}
                eventStatus={selected?.status}
                onSummary={setSummary}
              />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default RewardCoinApproval;
